import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import './App.css';

// Layout
import Header from './components/Layout/Header';
import LoadingOverlay from './components/Layout/LoadingOverlay';

// Pages
import HelpPage from './components/Pages/HelpPage';
import MainPage from './components/Pages/MainPage';
import StatusPage from './components/Pages/StatusPage';
import DebugLoginPage from './components/Pages/DebugLoginPage';
// import LandscapeWarning from './components/Pages/LandscapeWarning';
import TermsOfServicePage from './components/Pages/TermsOfServicePage';

// Modals
import CallModal from './components/Modals/CallModal';
import NotificationModal from './components/Modals/NotificationModal';
import AddFavoriteModal from './components/Modals/AddFavoriteModal';
import EditFavoriteModal from './components/Modals/EditFavoriteModal';
import DeleteFavoriteModal from './components/Modals/DeleteFavoriteModal';
import RegNoModal from './components/Modals/RegNoModal';

// Overlays
import LocationInfoOverlay from './components/Overlays/LocationInfoOverlay';
import FavoritesOverlay from './components/Overlays/FavoritesOverlay';
import ErrorToast from './components/Overlays/ErrorToast';
import TutorialOverlay from './components/Overlays/TutorialOverlay';

// Services
import ApiService from './services/api';

// Hooks
import useLiff from './hooks/useLiff';
import useWebSocket from './hooks/useWebSocket';
import useApi from './hooks/useApi';
import useFloorSelection from './hooks/useFloorSelection';
// import { useScreenOrientation } from './hooks/useScreenOrientation';


function App() {
  // State管理
  const [loading, setLoading] = useState(true);
  const [, setCurrentPage] = useState(1);
  const [activeOverlay, setActiveOverlay] = useState(null);
  const [currentRegNumber, setCurrentRegNumber] = useState(null);
  const [currentElevatorData, setCurrentElevatorData] = useState(null);
  const [myElevators, setMyElevators] = useState([]);
  const [handicap, setHandicap] = useState(false);
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const [callingElevator, setCallingElevator] = useState(false);
  const [, setSwiperInstance] = useState(null);

  // URLクエリパラメータから初期表示ページを決定
  const [initialSlideIndex] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const page = params.get('page');
    if (page === 'status') return 2;
    if (page === 'help') return 0;
    return 1; // デフォルトはMainPage
  });

  // Modal states
  const [showCallModal, setShowCallModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showAddFavoriteModal, setShowAddFavoriteModal] = useState(false);
  const [showEditFavoriteModal, setShowEditFavoriteModal] = useState(false);
  const [showDeleteFavoriteModal, setShowDeleteFavoriteModal] = useState(false);
  const [showRegNoModal, setShowRegNoModal] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorToast, setErrorToast] = useState({ title: '', message: '' });
  const [currentEditingElevator, setCurrentEditingElevator] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);

  const [, setLastCallInfo] = useState(null);

  // Custom Hooks
  const {
    isLiffReady,
    liffError,
    debugInfo,
    needsDebugLogin,
    isDebugMode,
    isAuthRedirecting,
    handleDebugLogin,
    liffService
  } = useLiff();

  const {
    wsConnected,
    messages
  } = useWebSocket(isLiffReady);

  const {
    fetchMyElevators,
    fetchElevatorDetails,
    callElevatorAPI,
    addNewElevatorAPI,
    updateElevatorNameAPI, updateNotificationSettingAPI, deleteElevatorAPI,
    updateMyElevatorsOrderAPI,
  } = useApi();

  const {
    selectedCurrentFloor,
    selectedDestinationFloor,
    currentSelectionMode,
    setSelectedCurrentFloor,
    setSelectedDestinationFloor,
    setCurrentSelectionMode,
    handleFloorClick,
    handleSwapFloors,
    handleCurrentFloorClick,
    handleDestinationFloorClick,
    handlePlaceholderCancel,
    resetForNewElevator,
    getInstructionText,
    areBothFloorsSelected,
    shouldShowCallReady,
  } = useFloorSelection(handicap);

  const myElevatorsRef = useRef(myElevators);
  const initialRegNumberRef = useRef(null);

  useEffect(() => {
    myElevatorsRef.current = myElevators;
  }, [myElevators]);

  // ===== 利用規約の同意処理 =====
  const handleAgreeTerms = async () => {
    try {
      // 1. バックエンド(DynamoDB)に保存
      await ApiService.updateUserPreferences({ termsAccepted: true });

      // 2. ステートを更新して初期化を再実行（エレベータ選択等の続きを実行）
      setTermsAgreed(true);
      await initializeApp();
    } catch (error) {
      console.error("利用規約の同意保存に失敗しました", error);
      setErrorToast({
        title: 'エラー',
        message: '利用規約の同意保存に失敗しました。'
      });
      setShowErrorToast(true);
    }
  };

  const handleDeclineTerms = () => {
    // 同意しない場合はLINEミニアプリ自体を閉じる
    if (window.liff) {
      window.liff.closeWindow();
    }
  };

  const handleCloseTutorial = async (dontShowAgain) => {
    setShowTutorial(false);
    if (dontShowAgain) {
      try {
        await ApiService.updateUserPreferences({ tutorialShown: true });
      } catch (error) {
        console.error("チュートリアル設定の保存に失敗しました", error);
      }
    }
    // チュートリアル終了後に初期エレベータ選択を実行（未選択時のみ）
    if (!currentRegNumber) {
      await selectInitialElevator();
    }
  };

  const handleShowTutorialAgain = () => {
    setShowTutorial(true);
  };

  // WebSocketメッセージの監視
  const processedMessageIndexRef = useRef(-1);

  useEffect(() => {
    if (messages.length === 0) return;

    const latestIndex = messages.length - 1;

    // 既に処理済みのメッセージはスキップ
    if (latestIndex <= processedMessageIndexRef.current) return;
    processedMessageIndexRef.current = latestIndex;

    const latestMessage = messages[latestIndex];

    // エレベータ呼出の完了/失敗（呼出中の場合のみ処理）
    if (latestMessage.status === 'completed') {
      setCallingElevator(prev => {
        if (prev) setShowCallModal(true);
        return false;
      });
    } else if (latestMessage.status === 'failed') {
      setCallingElevator(false);
      setErrorToast({
        title: '利用できません',
        message: 'エレベータの状況を確認してください。'
      });
      setShowErrorToast(true);
    }

    // 停止予定日(stopdate)の変更通知 → 対象エレベータ選択中なら詳細を再取得
    if (latestMessage.action === 'update_stopdate') {
      const targetRegno = latestMessage.regno;
      if (targetRegno && targetRegno === currentRegNumber) {
        // 現在表示中のエレベータの停止予定日が変更された → 詳細再取得
        fetchElevatorDetails(targetRegno).then(result => {
          if (result.success) {
            setCurrentElevatorData(result.data);
          }
        });
      }
    }

    // 運行状況(evStatus)の変更通知 → 対象エレベータ選択中なら詳細を再取得
    if (latestMessage.action === 'update_evstatus') {
      const targetRegno = latestMessage.regno;
      if (targetRegno && targetRegno === currentRegNumber) {
        fetchElevatorDetails(targetRegno).then(result => {
          if (result.success) {
            setCurrentElevatorData(result.data);
          }
        });
      }
    }
  }, [messages, currentRegNumber, fetchElevatorDetails]);

  // Swap機能をグローバルに公開
  useEffect(() => {
    window.reactSwapFloors = handleSwapFloors;
    return () => {
      delete window.reactSwapFloors;
    };
  }, [handleSwapFloors]);

  // const { isLandscape } = useScreenOrientation();

  // Initialize app
  const initializeApp = useCallback(async () => {
    try {
      setLoading(true);

      const token = liffService.getIDToken();
      ApiService.setToken(token);

      const result = await fetchMyElevators();

      if (!result.success) {
        setErrorToast(result.error);
        setShowErrorToast(true);
        setLoading(false);
        return;
      }

      const elevators = Array.isArray(result.elevators) ? result.elevators : [];
      setMyElevators(elevators);
      myElevatorsRef.current = elevators;

      // 利用規約に未同意の場合は、強制的に規約画面を表示する
      if (result.termsAccepted) {
        setTermsAgreed(true);
      } else {
        setTermsAgreed(false);
        setLoading(false);
        return;
      }

      // チュートリアル表示後にエレベータ選択を実施するため、currentRegNumberを保持
      initialRegNumberRef.current = result.currentRegNumber || null;

      // チュートリアル未表示の場合は表示（エレベータ選択はチュートリアル終了後に実行）
      if (!result.tutorialShown) {
        setLoading(false);
        setShowTutorial(true);
        return;
      }

      // チュートリアル表示済みの場合は直接エレベータ選択を実行
      await selectInitialElevator();

      setLoading(false);
    } catch (error) {
      console.error('アプリ初期化エラー:', error);
      setErrorToast({
        title: 'エラー',
        message: 'アプリの初期化に失敗しました。'
      });
      setShowErrorToast(true);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liffService, fetchMyElevators]);

  useEffect(() => {
    if (isLiffReady && liffService) {
      initializeApp();
    }
  }, [isLiffReady, liffService, initializeApp]);

  const handleSelectElevator = useCallback(async (regNumber) => {
    try {
      // setCurrentRegNumber(regNumber);

      // まず階情報を常にリセット
      resetForNewElevator();

      // 呼出中/呼出完了モーダルをリセット
      setCallingElevator(false);
      setShowCallModal(false);

      // マイエレベータリストから選択したエレベータを探す
      const selectedElevator = myElevatorsRef.current.find(
        elevator => elevator.regNumber === regNumber
      );

      if (selectedElevator) {
        // notify情報を更新
        setNotificationEnabled(selectedElevator.notify || false);

        // lastCall情報を更新
        if (selectedElevator.lastCall) {
          setLastCallInfo(selectedElevator.lastCall);
          // 階情報を自動設定
          const { startFloor, destFloor, handicapped } = selectedElevator.lastCall;

          if (startFloor && destFloor) {
            // console.log('前回の呼び出し情報を復元:', {
            //   startFloor,
            //   destFloor,
            //   handicapped
            // });

            // 階選択状態を設定
            setSelectedCurrentFloor(startFloor);
            setSelectedDestinationFloor(destFloor);
            setCurrentSelectionMode('call_ready');

            // 車椅子モードを設定
            setHandicap(handicapped);
          } else {
            // 階情報が不完全な場合はリセット
            resetForNewElevator();
          }
        } else {
          setLastCallInfo(null);
          resetForNewElevator();
        }
      } else {
        setLastCallInfo(null);
        resetForNewElevator();
      }

      const result = await fetchElevatorDetails(regNumber);

      if (result.success) {
        setCurrentRegNumber(regNumber);
        setCurrentElevatorData(result.data);
        // selectedElevator がない場合のみ、デフォルトで false を設定
        if (!selectedElevator) {
          setNotificationEnabled(false);
        }

        // 防御: バックエンドは evcallEnable=false / レコードなし の両方で
        // error_code="service_disabled" を返すため、通常ここには到達しない。
        // 万一 callev_available=false のデータが返った場合のフォールバック。
        if (result.data && !result.data.callev_available) {
          resetForNewElevator();
          setCurrentRegNumber(null);
          setCurrentElevatorData(null);
          setErrorToast({
            title: 'サービス対象外です',
            message: '番号に間違いがないか再度確認ください。\n※管理者の許可がないエレベータは利用できません'
          });
          setShowErrorToast(true);
          setActiveOverlay(null);
          return;
        }

        // エレベータ詳細取得後、マイエレベータリストにない場合はAddFavoriteModalを表示
        if (!selectedElevator) {
          setShowAddFavoriteModal(true);
        }
      } else {
        // エレベータ詳細取得に失敗した場合、階選択を初期状態にリセット
        resetForNewElevator();
        setCurrentRegNumber(null);
        setCurrentElevatorData(null);
        setErrorToast(result.error);
        setShowErrorToast(true);
      }

      setActiveOverlay(null);
    } catch (error) {
      console.error('エレベータ選択エラー:', error);
      setErrorToast({
        title: 'エラー',
        message: 'エレベータ情報の取得に失敗しました。'
      });
      setShowErrorToast(true);
    }
  }, [
    fetchElevatorDetails,
    setSelectedCurrentFloor,
    setSelectedDestinationFloor,
    setCurrentSelectionMode,
    setHandicap,
    resetForNewElevator
  ]);

  const selectInitialElevator = useCallback(async () => {
    const elevators = myElevatorsRef.current;

    setLoading(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const regnoParam = urlParams.get('regno');
      const isValidRegno = regnoParam && /^\d{7}$/.test(regnoParam);

      if (isValidRegno) {
        const selectedElevator = elevators.find(e => e.regNumber === regnoParam);
        if (selectedElevator) {
          await handleSelectElevator(selectedElevator.regNumber);
          if (selectedElevator.lastCall) {
            setLastCallInfo(selectedElevator.lastCall);
          }
        } else {
          await handleSelectElevator(regnoParam);
        }
      } else if (initialRegNumberRef.current) {
        const selectedElevator = elevators.find(e => e.regNumber === initialRegNumberRef.current);
        if (selectedElevator) {
          await handleSelectElevator(selectedElevator.regNumber);
          if (selectedElevator.lastCall) {
            setLastCallInfo(selectedElevator.lastCall);
          }
        } else {
          await handleSelectElevator(initialRegNumberRef.current);
        }
      } else if (elevators.length > 0) {
        await handleSelectElevator(elevators[0].regNumber);
        if (elevators[0].lastCall) {
          setLastCallInfo(elevators[0].lastCall);
        }
      }
    } catch (error) {
      console.error('初期エレベータ選択エラー:', error);
      setErrorToast({
        title: 'エラー',
        message: 'エレベータ情報の取得に失敗しました。'
      });
      setShowErrorToast(true);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSelectElevator]);

  const handleCallElevator = useCallback(async () => {
    if (!selectedCurrentFloor || !selectedDestinationFloor) {
      setErrorToast({
        title: 'エラー',
        message: '現在階と行先階を選択してください。'
      });
      setShowErrorToast(true);
      return;
    }

    setCallingElevator(true);

    const result = await callElevatorAPI(
      currentRegNumber,
      selectedCurrentFloor,
      selectedDestinationFloor,
      handicap
    );

    if (!result.success) {
      // レート制限エラーの場合
      if (result.isRateLimited) {
        setCallingElevator(false);
        // レート制限終了時刻をlocalStorageに保存（現在時刻 + 60秒）
        const rateLimitUntil = Date.now() + 60000;
        console.log('429 Error - Saving rate_limit_until:', rateLimitUntil);
        localStorage.setItem('rate_limit_until', rateLimitUntil.toString());
        console.log('Saved to localStorage:', localStorage.getItem('rate_limit_until'));
        // エラートーストも表示
        setErrorToast({
          title: '利用できません',
          message: 'しばらくしてから、再度呼び出してください。'
        });
        setShowErrorToast(true);
        // MainPageのレート制限UIも起動
        return { success: false, isRateLimited: true, error: result.error };
      }

      setCallingElevator(false);
      setErrorToast(result.error);
      setShowErrorToast(true);
      return;
    }

    // WebSocketの応答待ち
  }, [currentRegNumber, selectedCurrentFloor, selectedDestinationFloor, handicap, callElevatorAPI]);

  const handleAddFavorite = useCallback(async (regNumber, name) => {
    const result = await addNewElevatorAPI(regNumber, name);

    if (!result.success) {
      setErrorToast(result.error);
      setShowErrorToast(true);
      return;
    }

    const refreshResult = await fetchMyElevators();

    if (refreshResult.success) {
      const elevators = Array.isArray(refreshResult.elevators) ? refreshResult.elevators : [];
      setMyElevators(elevators);
      myElevatorsRef.current = elevators;

      setActiveOverlay('favorites');
    }
  }, [addNewElevatorAPI, fetchMyElevators, setActiveOverlay]);

  const handleUpdateElevatorName = useCallback(async (regNumber, newName) => {
    const result = await updateElevatorNameAPI(regNumber, newName);

    if (!result.success) {
      setErrorToast(result.error);
      setShowErrorToast(true);
      return;
    }

    setShowEditFavoriteModal(true);
    setCurrentEditingElevator(null);
    const refreshResult = await fetchMyElevators();

    if (refreshResult.success) {
      const elevators = Array.isArray(refreshResult.elevators) ? refreshResult.elevators : [];
      setMyElevators(elevators);
      myElevatorsRef.current = elevators;
      setActiveOverlay('favorites');
    }
  }, [updateElevatorNameAPI, fetchMyElevators, setActiveOverlay]);

  const handleDeleteElevator = useCallback(async (regNumber) => {
    const result = await deleteElevatorAPI(regNumber);

    if (!result.success) {
      setErrorToast(result.error);
      setShowErrorToast(true);
      return;
    }

    setShowDeleteFavoriteModal(true);
    setCurrentEditingElevator(null);

    const refreshResult = await fetchMyElevators();

    if (refreshResult.success) {
      const elevators = Array.isArray(refreshResult.elevators) ? refreshResult.elevators : [];
      setMyElevators(elevators);
      myElevatorsRef.current = elevators;
      setActiveOverlay('favorites');
    }

    if (currentRegNumber === regNumber) {
      setCurrentRegNumber(null);
      setCurrentElevatorData(null);
      resetForNewElevator();
    }
  }, [currentRegNumber, deleteElevatorAPI, fetchMyElevators, resetForNewElevator]);

  const handleReorderElevators = useCallback(async (order) => {
    const result = await updateMyElevatorsOrderAPI(order);

    if (!result.success) {
      setErrorToast(result.error);
      setShowErrorToast(true);

      // エラー時は元の順序に戻すためリフレッシュ
      const refreshResult = await fetchMyElevators();
      if (refreshResult.success) {
        const elevators = Array.isArray(refreshResult.elevators) ? refreshResult.elevators : [];
        setMyElevators(elevators);
        myElevatorsRef.current = elevators;
      }
      return;
    }

    // 成功時はローカルの状態を更新
    const refreshResult = await fetchMyElevators();
    if (refreshResult.success) {
      const elevators = Array.isArray(refreshResult.elevators) ? refreshResult.elevators : [];
      setMyElevators(elevators);
      myElevatorsRef.current = elevators;
    }
  }, [updateMyElevatorsOrderAPI, fetchMyElevators]);

  const handleCallModalClose = useCallback(() => {
    setShowCallModal(false);
    setLastCallInfo({
      startFloor: selectedCurrentFloor,
      destFloor: selectedDestinationFloor,
      handicapped: handicap,
      updatedAt: new Date().toISOString()
    });
  }, [selectedCurrentFloor, selectedDestinationFloor, handicap]);

  const handleNotificationToggle = useCallback(async () => {
    const newNotificationState = !notificationEnabled;

    const result = await updateNotificationSettingAPI(currentRegNumber, newNotificationState);

    if (result.success) {
      setNotificationEnabled(newNotificationState);
      setShowNotificationModal(false);

      // マイエレベータリストを更新して最新の通知設定を反映
      const refreshResult = await fetchMyElevators();
      if (refreshResult.success) {
        const elevators = Array.isArray(refreshResult.elevators) ? refreshResult.elevators : [];
        setMyElevators(elevators);
        myElevatorsRef.current = elevators;
      }
    } else {
      setShowNotificationModal(false);
      setErrorToast(result.error);
      setShowErrorToast(true);
    }
  }, [notificationEnabled, currentRegNumber, updateNotificationSettingAPI, fetchMyElevators]);

  if (needsDebugLogin && isDebugMode) {
    return <DebugLoginPage onDebugLogin={handleDebugLogin} />;
  }

  // 横画面の場合は警告画面を表示
  // if (isLandscape) {
  //   return <LandscapeWarning isVisible={true} />;
  // }

  if (liffError) {
    return (
      <div className="w-screen h-screen flex flex-col items-center justify-center bg-gray-100 p-8">
        <h1 className="text-2xl font-bold text-red-600 mb-4">利用できません</h1>
        <p className="text-gray-600 text-center mb-4">{liffError}</p>
        {debugInfo && isDebugMode && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg text-xs text-left max-w-md overflow-auto max-h-60">
            <h3 className="font-bold mb-2">デバッグ情報:</h3>
            <pre className="whitespace-pre-wrap">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </div>
        )}
      </div>
    );
  }

  if (!isLiffReady || loading || isAuthRedirecting) {
    const loadingMessage = "読み込み中...";
    return <LoadingOverlay message={loadingMessage} />;
  }

  // 利用規約未同意の場合は規約画面を表示
  if (!termsAgreed) {
    return (
      <TermsOfServicePage
        onAgree={handleAgreeTerms}
        onDecline={handleDeclineTerms}
      />
    );
  }

  return (
    <div className="fixed h-screen w-screen overflow-hidden flex flex-col mx-auto shadow-lg ux-bg-light-gray">
      <div className={`fixed top-2 left-2 z-[100] ${wsConnected ? 'hidden' : ''}`}>
        <div className="bg-red-500 w-3 h-3 rounded-full"></div>
      </div>

      <Header
        activeOverlay={activeOverlay}
        setActiveOverlay={setActiveOverlay}
        myElevators={myElevators}
        onSelectElevator={handleSelectElevator}
        currentRegNumber={currentRegNumber}
        currentElevatorData={currentElevatorData}
      />

      <main className="flex-grow h-[calc(100svh/24*20)] mt-[calc(100svh/72*2)] fixed top-[calc(100svh/72*7)] left-0 right-0 bottom-0">
        <Swiper
          spaceBetween={16}
          slidesPerView={1.2}
          centeredSlides={true}
          onSlideChange={(swiper) => setCurrentPage(swiper.activeIndex)}
          onSwiper={(swiper) => {
            setSwiperInstance(swiper);
            setCurrentPage(initialSlideIndex);
          }}
          className="h-full w-full"
          initialSlide={initialSlideIndex}
          allowTouchMove={true}
          touchStartPreventDefault={false}
          touchAngle={35}
          threshold={5}
          loop={true}
          modules={[Pagination]}
          pagination={{ clickable: true }}
          onTouchStart={(swiper, event) => {
            const target = event.target;
            if (target.closest('#color-switcher') ||
              target.closest('#call-button') ||
              target.closest('.notification-toggle') ||
              target.closest('button')) {
              swiper.allowTouchMove = false;
            } else {
              swiper.allowTouchMove = true;
            }
          }}
          onTouchEnd={(swiper) => {
            swiper.allowTouchMove = true;
          }}
        >
          <SwiperSlide>
            <HelpPage onShowTutorial={handleShowTutorialAgain} />
          </SwiperSlide>
          <SwiperSlide>
            <MainPage
              currentElevatorData={currentElevatorData}
              selectedCurrentFloor={selectedCurrentFloor}
              selectedDestinationFloor={selectedDestinationFloor}
              currentSelectionMode={currentSelectionMode}
              handicap={handicap}
              setHandicap={setHandicap}
              onFloorClick={handleFloorClick}
              onCallElevator={handleCallElevator}
              onSwapFloors={handleSwapFloors}
              onCurrentFloorClick={handleCurrentFloorClick}
              onDestinationFloorClick={handleDestinationFloorClick}
              onPlaceholderCancel={handlePlaceholderCancel}
              currentRegNumber={currentRegNumber}
              getInstructionText={() => getInstructionText(currentRegNumber)}
              areBothFloorsSelected={areBothFloorsSelected}
              shouldShowCallReady={shouldShowCallReady}
            />
          </SwiperSlide>
          <SwiperSlide>
            <StatusPage
              currentElevatorData={currentElevatorData}
              notificationEnabled={notificationEnabled}
              onToggleNotification={() => setShowNotificationModal(true)}
            />
          </SwiperSlide>
        </Swiper>
      </main>

      <LocationInfoOverlay
        isOpen={activeOverlay === 'location'}
        onClose={() => setActiveOverlay(null)}
      />

      <FavoritesOverlay
        isOpen={activeOverlay === 'favorites'}
        myElevators={myElevators}
        currentRegNumber={currentRegNumber}
        onClose={() => setActiveOverlay(null)}
        onSelectElevator={handleSelectElevator}
        onEditElevator={(elevator) => {
          setCurrentEditingElevator(elevator);
          setShowEditFavoriteModal(true);
          setActiveOverlay(null);
        }}
        onDeleteElevator={(elevator) => {
          setCurrentEditingElevator(elevator);
          setShowDeleteFavoriteModal(true);
          setActiveOverlay(null);
        }}
        onElevatorInfo={(elevator) => {
          setCurrentEditingElevator(elevator);
          setShowRegNoModal(true);
          setActiveOverlay(null);
        }}
        onReorderElevators={handleReorderElevators}
      />

      {callingElevator && (
        <LoadingOverlay message="エレベータを呼び出しています..." isVisible={true} />
      )}

      <CallModal
        isOpen={showCallModal}
        onClose={handleCallModalClose}
        currentFloor={selectedCurrentFloor}
        destinationFloor={selectedDestinationFloor}
        floors={currentElevatorData?.floors}
      />

      <NotificationModal
        isOpen={showNotificationModal}
        onClose={() => setShowNotificationModal(false)}
        notificationEnabled={notificationEnabled}
        onConfirm={handleNotificationToggle}
      />

      <AddFavoriteModal
        isOpen={showAddFavoriteModal}
        onClose={() => {
          setShowAddFavoriteModal(false);
          setActiveOverlay(null);
        }}
        regNumber={currentRegNumber}
        onAdd={handleAddFavorite}
      />

      <EditFavoriteModal
        isOpen={showEditFavoriteModal}
        onBack={() => { setShowEditFavoriteModal(false); setActiveOverlay('favorites'); }}
        onClose={() => { setShowEditFavoriteModal(false); setActiveOverlay(null); }}
        elevator={currentEditingElevator}
        onSave={handleUpdateElevatorName}
      />

      <DeleteFavoriteModal
        isOpen={showDeleteFavoriteModal}
        onBack={() => { setShowDeleteFavoriteModal(false); setActiveOverlay('favorites'); }}
        onClose={() => { setShowDeleteFavoriteModal(false); setActiveOverlay(null); }}
        elevator={currentEditingElevator}
        onConfirm={handleDeleteElevator}
      />

      <RegNoModal
        isOpen={showRegNoModal}
        onClose={() => { setShowRegNoModal(false); setActiveOverlay(null); }}
        onOK={() => { setShowRegNoModal(false); setActiveOverlay('favorites'); }}
        elevator={currentEditingElevator}
      />

      <ErrorToast
        isOpen={showErrorToast}
        title={errorToast.title}
        message={errorToast.message}
        onClose={() => setShowErrorToast(false)}
      />

      <TutorialOverlay
        isOpen={showTutorial}
        onClose={handleCloseTutorial}
      />
    </div>
  );
}

export default App;